import React, { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { FaStar } from "react-icons/fa";
import axios from "axios";

const ratingLabels = {
  1: "Very Poor",
  2: "Poor",
  3: "Average",
  4: "Good",
  5: "Excellent",
};

// Reusable Star Rating Component for Questions
const QuestionStarRating = ({
  question,
  rating,
  setRating,
  hoverRating,
  setHoverRating,
}) => {
  return (
    <div className="mt-3 p-4 bg-gradient-to-br from-white to-[#f8fdfb] rounded-xl border border-[#e0f2e9] shadow-sm hover:shadow-md transition-all duration-300 hover:border-[#00bd7f]/30">
      <span className="block text-base font-semibold text-[#004276] mb-3 leading-relaxed">
        {question}
      </span>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {[1, 2, 3, 4, 5].map((value) => (
            <button
              key={value}
              type="button"
              className="focus:outline-none transform transition-transform hover:scale-110 active:scale-95"
              onClick={() => setRating(value)}
              onMouseEnter={() => setHoverRating(value)}
              onMouseLeave={() => setHoverRating(0)}
              aria-label={`Rate ${value} star${value > 1 ? "s" : ""}`}
            >
              <FaStar
                className="text-2xl transition-all duration-200 drop-shadow-sm"
                color={
                  (hoverRating || rating) >= value 
                    ? "#ffc107" 
                    : "#d1d5db"
                }
                style={{
                  filter: (hoverRating || rating) >= value 
                    ? "drop-shadow(0 2px 4px rgba(255, 193, 7, 0.3))" 
                    : "none"
                }}
              />
            </button>
          ))}
        </div>
        <div className="flex-1 min-w-[120px]">
          <span className={`text-sm font-medium px-3 py-1.5 rounded-full inline-block ${
            rating 
              ? rating >= 4 
                ? "bg-green-100 text-green-700" 
                : rating >= 3 
                ? "bg-yellow-100 text-yellow-700" 
                : "bg-red-100 text-red-700"
              : "bg-gray-100 text-gray-500"
          }`}>
            {rating ? ratingLabels[rating] : "Select rating"}
          </span>
        </div>
      </div>
    </div>
  );
};

const defaultExperienceOptions = [
  "Consultation",
  "Clinic Environment",
  "Staff Behaviour",
  "Waiting Time",
  "Booking Process",
  "Overall Experience",
];

const PatientFeedback = () => {
  const { clinicId } = useParams();
  const [searchParams] = useSearchParams();
  const appointmentId = useMemo(
    () => searchParams.get("appointmentId") || "",
    [searchParams]
  );
  const API_URL = process.env.REACT_APP_API_URL;
  const [patientName, setPatientName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [experienceArea, setExperienceArea] = useState(
    defaultExperienceOptions[0]
  );
  const [comments, setComments] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  
  // New question ratings
  const [bookingRating, setBookingRating] = useState(0);
  const [bookingHover, setBookingHover] = useState(0);
  const [staffRating, setStaffRating] = useState(0);
  const [staffHover, setStaffHover] = useState(0);
  const [explanationRating, setExplanationRating] = useState(0);
  const [explanationHover, setExplanationHover] = useState(0);
  const [listeningRating, setListeningRating] = useState(0);
  const [listeningHover, setListeningHover] = useState(0);
  const [recommendRating, setRecommendRating] = useState(0);
  const [recommendHover, setRecommendHover] = useState(0);

  if (!clinicId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f9f7] px-4">
        <div className="max-w-lg w-full bg-white shadow-md rounded-xl p-6 text-center">
          <h1 className="text-xl font-semibold text-[#004276] mb-2">
            Clinic link incomplete
          </h1>
          <p className="text-sm text-gray-600">
            The feedback link you opened is missing clinic information. Please
            double-check the link shared with you.
          </p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (
      !bookingRating ||
      !staffRating ||
      !explanationRating ||
      !listeningRating ||
      !recommendRating
    ) {
      setSubmitStatus({
        type: "error",
        message: "Please answer all questions before submitting.",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const payload = {
        patientName: patientName || undefined,
        whatsappNumber: contactNumber || undefined,
        areaOfExperience: experienceArea,
        q1: bookingRating,
        q2: staffRating,
        q3: explanationRating,
        q4: listeningRating,
        q5: recommendRating,
        overallFeedback: comments || undefined,
        appointmentId: appointmentId || undefined,
      };

      await axios.post(
        `${API_URL}consultation/feedback/submit`,
        payload,
        {
          headers: {
            tenantId: clinicId,
            "Content-Type": "application/json",
          },
        }
      );

      setSubmitStatus({
        type: "success",
        message:
          "Thank you for sharing your feedback. We truly appreciate your time!",
      });
      setPatientName("");
      setContactNumber("");
      setExperienceArea(defaultExperienceOptions[0]);
      setComments("");
      setBookingRating(0);
      setBookingHover(0);
      setStaffRating(0);
      setStaffHover(0);
      setExplanationRating(0);
      setExplanationHover(0);
      setListeningRating(0);
      setListeningHover(0);
      setRecommendRating(0);
      setRecommendHover(0);
    } catch (error) {
      console.error("Patient feedback submission failed:", error);
      const message =
        error.response?.data?.message ||
        "Unable to submit feedback right now. Please try again in a moment.";
      setSubmitStatus({ type: "error", message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f0f9f5] via-[#f5f9f7] to-[#e8f5f0] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-3xl bg-white shadow-2xl rounded-3xl border border-[#d7ebe2] overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-[#004276] via-[#005a9e] to-[#00bd7f] text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24"></div>
          <div className="relative z-10">
            <h1 className="text-2xl font-bold mb-1">We value your feedback</h1>
            <p className="text-sm opacity-95 font-medium">
              Help us improve your experience at our clinic by sharing your
              thoughts.
            </p>
          </div>
        </div>

        <form className="p-6" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-[#004276] mb-1.5">
                Your Name <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                className="w-full h-11 px-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#00bd7f] focus:border-[#00bd7f] transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                placeholder="Enter your name"
                value={patientName}
                onChange={(event) => setPatientName(event.target.value)}
                maxLength={80}
              />
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-[#004276] mb-1.5">
                WhatsApp / Phone <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="tel"
                className="w-full h-11 px-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#00bd7f] focus:border-[#00bd7f] transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                placeholder="Enter your contact number"
                value={contactNumber}
                onChange={(event) => setContactNumber(event.target.value)}
                maxLength={15}
              />
            </div>
          </div>

          <div className="mb-5">
            <label className="block text-sm font-semibold text-[#004276] mb-1.5">
              Area of experience
            </label>
            <select
              className="w-full h-11 px-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#00bd7f] focus:border-[#00bd7f] transition-all duration-200 bg-white shadow-sm hover:shadow-md"
              value={experienceArea}
              onChange={(event) => setExperienceArea(event.target.value)}
            >
              {defaultExperienceOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-5 pt-5 border-t-2 border-[#00bd7f]/30">
            
            
            <div className="space-y-3">
              <QuestionStarRating
                question="How smooth was your appointment booking and check-in? (1–5)"
                rating={bookingRating}
                setRating={setBookingRating}
                hoverRating={bookingHover}
                setHoverRating={setBookingHover}
              />

              <QuestionStarRating
                question="How courteous and helpful were the clinic staff? (1–5)"
                rating={staffRating}
                setRating={setStaffRating}
                hoverRating={staffHover}
                setHoverRating={setStaffHover}
              />

              <QuestionStarRating
                question="How clearly did the doctor explain your condition and next steps? (1–5)"
                rating={explanationRating}
                setRating={setExplanationRating}
                hoverRating={explanationHover}
                setHoverRating={setExplanationHover}
              />

              <QuestionStarRating
                question="How well did the doctor listen and make you feel cared for? (1–5)"
                rating={listeningRating}
                setRating={setListeningRating}
                hoverRating={listeningHover}
                setHoverRating={setListeningHover}
              />

              <QuestionStarRating
                question="Overall, how likely are you to recommend our clinic to others? (1–5)"
                rating={recommendRating}
                setRating={setRecommendRating}
                hoverRating={recommendHover}
                setHoverRating={setRecommendHover}
              />
            </div>
          </div>

          <div className="mt-5">
            <label className="block text-sm font-semibold text-[#004276] mb-1.5">
              Overall feedback <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              className="w-full min-h-[120px] px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#00bd7f] focus:border-[#00bd7f] transition-all duration-200 bg-white shadow-sm hover:shadow-md resize-y"
              placeholder="Share any additional comments"
              value={comments}
              onChange={(event) => setComments(event.target.value)}
              maxLength={1000}
            />
            <div className="text-xs text-gray-400 text-right mt-1">
              {comments.length}/1000
            </div>
          </div>

         
          {submitStatus && (
            <div
              className={`mt-4 rounded-xl px-4 py-3 text-sm font-medium shadow-md border-2 ${
                submitStatus.type === "success"
                  ? "bg-gradient-to-r from-green-50 to-emerald-50 text-green-800 border-green-200"
                  : "bg-gradient-to-r from-red-50 to-rose-50 text-red-800 border-red-200"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className={`text-lg ${submitStatus.type === "success" ? "text-green-600" : "text-red-600"}`}>
                  {submitStatus.type === "success" ? "✓" : "✕"}
                </span>
                {submitStatus.message}
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="mt-5 w-full h-12 rounded-xl bg-gradient-to-r from-[#004276] via-[#005a9e] to-[#00bd7f] text-white font-bold text-base flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Submitting...
              </>
            ) : (
              "Submit Feedback"
            )}
          </button>

         
        </form>
      </div>
    </div>
  );
};

export default PatientFeedback;

